// Organization profiles (PlatformDB 056): one business profile per product of the organization.
// Replaces the single "company public profile". The organization is the tenant; each profile is
// a business customers see, with its own public page, regional and invoicing settings.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useCan } from '../../core/authorization/PermissionProvider';
import { publicApi } from '../public/publicApi';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { hubApi, read, write, type CompanyText, type OrganizationProfile, type Values } from './hubApi';
import {
  EntityDialog,
  EntityGrid,
  errorMessage,
  HubSection,
  HubTitle,
  LoadState,
  ProductMark,
  useHubLoad,
  type Field,
} from './HubUi';
import Add from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNew from '@mui/icons-material/OpenInNew';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { ProfilePhotos } from './ProfilePhotos';
import { ProfileLocationSearch } from './ProfileLocationSearch';

export const PROFILES_PATH = '/app/organization-profiles';
const profilePath = (id: string) => `${PROFILES_PATH}/${encodeURIComponent(id)}`;
const apiPath = (id: string) => `/organization-profiles/${encodeURIComponent(id)}`;

const TIME_ZONES = [
  'Europe/Tirane',
  'Europe/Rome',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'Asia/Dubai',
  'UTC',
];
const CURRENCIES = ['ALL', 'EUR', 'USD', 'GBP', 'CHF'];
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The fields PUT/POST /organization-profiles accepts, in the order PlatformAPI declares them. */
const REQUEST_KEYS = [
  'registrationNumber', 'displayName', 'email', 'phone', 'website', 'slug', 'customUrl', 'addressLine', 'city',
  'postalCode', 'countryCode', 'latitude', 'longitude', 'instagramUrl', 'facebookUrl', 'tikTokUrl', 'workingHours',
  'isPublic', 'timeZone', 'currencyCode', 'isVatRegistered', 'defaultVatRate', 'bankTransactionPercentage',
  'menuKeyCode', 'licenseKey', 'licenseUrl', 'integrationId', 'orderConfirmationEmailEnabled',
] as const;

function toRequest(value: Values, productCode?: string) {
  const body: Record<string, unknown> = Object.fromEntries(
    REQUEST_KEYS.map((key) => [key, value[key] === '' || value[key] === undefined ? null : value[key]]),
  );
  body.isPublic = value.isPublic === true;
  body.isVatRegistered = value.isVatRegistered !== false;
  body.orderConfirmationEmailEnabled = value.orderConfirmationEmailEnabled === true;
  body.integrationId = value.integrationId === '' || value.integrationId == null ? null : Number(value.integrationId);
  if (productCode) body.productCode = productCode;
  return body;
}

const options = (values: string[], current?: string | null) =>
  Array.from(new Set([...(current ? [current] : []), ...values])).map((v) => ({ value: v, label: v }));

const publicFields: Field[] = [
  { key: 'displayName', label: 'Display name', maxLength: 200, helper: 'The business name customers see.' },
  { key: 'slug', label: 'Public slug', maxLength: 150, helper: 'The public page opens at /o/your-slug.' },
  { key: 'email', label: 'Email', type: 'email', maxLength: 320 },
  { key: 'phone', label: 'Phone', maxLength: 50 },
  { key: 'website', label: 'Website', type: 'url', maxLength: 500 },
  { key: 'addressLine', label: 'Street address', maxLength: 300 },
  { key: 'city', label: 'City', maxLength: 100 },
  { key: 'postalCode', label: 'Postal code', maxLength: 20 },
  { key: 'countryCode', label: 'Country code', maxLength: 2, helper: 'Two letters, for example AL.' },
  { key: 'workingHours', label: 'Working hours', maxLength: 500, helper: 'For example Mon–Sat 09:00–20:00.' },
  { key: 'instagramUrl', label: 'Instagram', type: 'url', maxLength: 500 },
  { key: 'facebookUrl', label: 'Facebook', type: 'url', maxLength: 500 },
  { key: 'tikTokUrl', label: 'TikTok', type: 'url', maxLength: 500 },
  {
    key: 'customUrl',
    label: 'Custom destination URL',
    type: 'url',
    maxLength: 500,
    helper: 'Visitors open this URL before the product website. Use HTTPS.',
  },
  { key: 'isPublic', label: 'Show this business in the public directory', type: 'boolean' },
];
const regionalFields = (p: OrganizationProfile): Field[] => [
  { key: 'timeZone', label: 'Time zone', type: 'select', required: true, options: options(TIME_ZONES, p.timeZone), helper: 'Used to display dates and times.' },
  { key: 'currencyCode', label: 'Currency', type: 'select', required: true, options: options(CURRENCIES, p.currencyCode) },
];
const invoicingFields: Field[] = [
  { key: 'registrationNumber', label: 'Registration number (NIPT)', maxLength: 100 },
  { key: 'defaultVatRate', label: 'Default VAT rate (%)', type: 'number', min: 0, max: 100, helper: '0–100. Leave empty when not set.' },
  { key: 'bankTransactionPercentage', label: 'Bank transaction (%)', type: 'number', min: 0, max: 100, helper: '0–100. Leave empty when not set.' },
  { key: 'isVatRegistered', label: 'VAT registered', type: 'boolean' },
];
type ProfileIntegration = { id: number; name: string; isActive: boolean };
const integrationFields = (p: OrganizationProfile, integrations: ProfileIntegration[]): Field[] => [
  { key: 'integrationId', label: 'Integration', type: 'select', options: integrations.map((i) => ({ value: String(i.id), label: `${i.name}${i.isActive ? '' : ' (inactive)'}` })) },
  ...(p.productCode === 'RESTAURANT'
    ? [{ key: 'menuKeyCode', label: 'Menu key code', maxLength: 100, helper: "Key the restaurant product uses to resolve this business's public menu." }]
    : []),
  { key: 'licenseKey', label: 'License key', maxLength: 200 },
  { key: 'licenseUrl', label: 'License URL', type: 'url', maxLength: 500 },
];

function validateProfile(value: Values, hasPrimaryCategory: boolean): string | null {
  const slug = String(value.slug ?? '').trim();
  if (slug && !SLUG.test(slug)) return 'Use lowercase letters, numbers and hyphens for the slug.';
  if (value.isPublic === true && (!slug || !String(value.city ?? '').trim()))
    return 'Add a public slug and a city before listing this business.';
  if (value.isPublic === true && !hasPrimaryCategory) return 'Choose a primary category before listing this business.';
  for (const key of ['defaultVatRate', 'bankTransactionPercentage'] as const) {
    const n = value[key];
    if (n !== null && n !== undefined && n !== '' && (Number(n) < 0 || Number(n) > 100)) return 'Percentages must be between 0 and 100.';
  }
  const country = String(value.countryCode ?? '').trim();
  if (country && country.length !== 2) return 'Use a two-letter country code.';
  for (const key of ['customUrl', 'website', 'instagramUrl', 'facebookUrl', 'tikTokUrl', 'licenseUrl'] as const) {
    if (!value[key]) continue;
    try {
      const url = new URL(String(value[key]));
      if ((url.protocol !== 'https:' && !(import.meta.env.DEV && url.protocol === 'http:')) || url.username || url.password)
        return 'Use HTTPS links with no credentials.';
    } catch {
      return 'Enter complete links, starting with https://.';
    }
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------------------------

export function OrganizationProfilesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canCreate = useCan('platform.company', 'create');
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.profiles(signal), []));
  const [create, setCreate] = useState<Values | null>(null);
  const requested = params.get('product')?.toUpperCase() ?? null;

  // Opened from a product ("Edit profile" on Cosmetics): go to its profile, or offer to create it.
  useEffect(() => {
    if (!requested || !load.data) return;
    const existing = load.data.items.find((item) => item.productCode === requested);
    if (existing) {
      navigate(profilePath(existing.id), { replace: true });
      return;
    }
    if (canCreate && load.data.availableProducts.some((product) => product.code === requested))
      setCreate({ productCode: requested, displayName: '', city: '', timeZone: 'Europe/Tirane', currencyCode: 'ALL' });
    setParams({}, { replace: true });
  }, [requested, load.data, canCreate, navigate, setParams]);

  const available = load.data?.availableProducts ?? [];
  return (
    <>
      <HubTitle
        title="Organization profiles"
        subtitle="One profile for each business your organization runs: its public page, regional and invoicing settings."
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!canCreate || available.length === 0}
            title={load.data && available.length === 0 ? 'Every enabled product already has a profile.' : undefined}
            onClick={() =>
              setCreate({ productCode: available.length === 1 ? available[0].code : '', displayName: '', city: '', timeZone: 'Europe/Tirane', currencyCode: 'ALL' })
            }
          >
            Add profile
          </Button>
        }
      />
      <LoadState {...load} />
      {load.data && (
        <EntityGrid
          rows={load.data.items}
          rowKey={(row) => row.id}
          columns={[
            {
              label: 'Business',
              render: (row) => (
                <Stack direction="row" gap={1.25} alignItems="center">
                  <ProductMark code={row.productCode} size={30} />
                  <span>{row.productName}</span>
                </Stack>
              ),
            },
            { label: 'Display name', render: (row) => row.displayName || '—' },
            { label: 'City', render: (row) => row.city || '—' },
            { label: 'Public page', render: (row) => (row.slug ? `/o/${row.slug}` : '—') },
            {
              label: 'Status',
              render: (row) => (
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  <Chip size="small" color={row.isPublic ? 'success' : 'default'} label={row.isPublic ? 'Listed' : 'Unlisted'} />
                  {!row.checklistComplete && <Chip size="small" variant="outlined" color="warning" label="Incomplete" />}
                </Stack>
              ),
            },
            { label: 'Updated', render: (row) => new Date(row.updatedAt).toLocaleDateString() },
          ]}
          actions={(row) => (
            <Button size="small" variant="outlined" startIcon={<EditOutlined />} component={Link} to={profilePath(row.id)}>
              Edit profile
            </Button>
          )}
          empty="No profiles yet. Add one for each product your organization runs."
        />
      )}
      {load.data && available.length > 0 && !canCreate && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          Your role cannot add profiles. {available.map((p) => p.displayName).join(', ')} has no profile yet.
        </Alert>
      )}
      {create && (
        <EntityDialog
          title="Add organization profile"
          value={create}
          close={() => setCreate(null)}
          fields={[
            {
              key: 'productCode',
              label: 'Business (product)',
              type: 'select',
              required: true,
              options: available.map((product) => ({ value: product.code, label: product.displayName })),
              helper: 'Each product of the organization has exactly one profile.',
            },
            { key: 'displayName', label: 'Display name', maxLength: 200 },
            { key: 'city', label: 'City', maxLength: 100 },
            { key: 'timeZone', label: 'Time zone', type: 'select', required: true, options: options(TIME_ZONES) },
            { key: 'currencyCode', label: 'Currency', type: 'select', required: true, options: options(CURRENCIES) },
          ]}
          validate={(value) => (value.productCode ? null : 'Choose the product this profile is for.')}
          save={async (value) => {
            const created = await write<OrganizationProfile>(
              '/organization-profiles',
              toRequest({ ...value, isVatRegistered: true, isPublic: false }, String(value.productCode)),
              'post',
            );
            navigate(profilePath(created.id));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// One profile
// ---------------------------------------------------------------------------------------------

type Section = { title: string; fields: Field[] };

export function OrganizationProfilePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canUpdate = useCan('platform.company', 'update');
  const canDelete = useCan('platform.company', 'delete');
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.profile(id, signal), [id]));
  const checklist = useHubLoad(useCallback((signal: AbortSignal) => hubApi.checklist(id, signal), [id]));
  const [edit, setEdit] = useState<Section | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const profile = load.data;
  const integrations = useHubLoad(useCallback((signal: AbortSignal) => read<ProfileIntegration[]>(`${apiPath(id)}/integrations`, undefined, signal), [id]));
  const hasPrimaryCategory = checklist.data?.items.find((item) => item.code === 'primaryCategory')?.complete ?? false;

  const sectionAction = (section: Section) => (
    <Button size="small" variant="outlined" startIcon={<EditOutlined />} disabled={!canUpdate} onClick={() => setEdit(section)}>
      Edit
    </Button>
  );

  return (
    <>
      <Button component={Link} to={PROFILES_PATH} startIcon={<ArrowBack />} size="small" sx={{ mb: 1 }}>
        All profiles
      </Button>
      <LoadState {...load} />
      {profile && (
        <>
          <HubTitle
            title={profile.displayName || profile.productName}
            subtitle={`${profile.productName} profile`}
            action={
              <Stack direction="row" gap={1} flexWrap="wrap">
                {profile.slug && profile.isPublic && (
                  <Button component={Link} to={`/o/${encodeURIComponent(profile.slug)}`} endIcon={<OpenInNew />}>
                    View as visitor
                  </Button>
                )}
                {canDelete && (
                  <Button color="error" startIcon={<DeleteOutline />} onClick={() => setConfirmDelete(true)}>
                    Delete profile
                  </Button>
                )}
              </Stack>
            }
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box className="hub-panel" sx={{ mb: 3 }}>
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <ProductMark code={profile.productCode} size={40} />
              <Typography variant="h6" component="p" sx={{ fontWeight: 800, mr: 'auto', minWidth: 0, overflowWrap: 'anywhere' }}>
                {profile.displayName || 'No display name yet'}
              </Typography>
              <Chip size="small" color={profile.isPublic ? 'success' : 'default'} label={profile.isPublic ? 'Listed' : 'Unlisted'} />
              {profile.isVerified && <Chip size="small" color="primary" label="Verified" />}
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
              {[profile.addressLine, profile.city].filter(Boolean).join(', ') || 'No address yet'}
            </Typography>
            <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {profile.customUrl ? `Destination: ${profile.customUrl}` : `Destination: ${profile.productName} website, then the Platform page`}
            </Typography>
          </Box>

          <HubSection
            title="Listing checklist"
            subtitle={
              checklist.data
                ? `${checklist.data.items.filter((item) => item.complete).length} of ${checklist.data.items.length} complete`
                : undefined
            }
          >
            <LoadState {...checklist} />
            {checklist.data && (
              <ul className="hub-checklist">
                {checklist.data.items.map((item) => (
                  <li key={item.code} className={item.complete ? 'done' : undefined}>
                    {item.complete ? (
                      <CheckCircle sx={{ fontSize: 20 }} titleAccess="Complete" />
                    ) : (
                      <RadioButtonUnchecked sx={{ fontSize: 20 }} titleAccess="Not complete" />
                    )}
                    {item.label}
                  </li>
                ))}
              </ul>
            )}
          </HubSection>

          {(
            [
              { title: 'Public profile', subtitle: 'How customers find this business and where its card opens.', fields: publicFields },
              { title: 'Regional', subtitle: 'Time zone and currency this business operates in.', fields: regionalFields(profile) },
              { title: 'Notifications', subtitle: 'Customer notifications for this business.', fields: [
                { key: 'orderConfirmationEmailEnabled', label: 'Order confirmation by email', type: 'boolean', helper: 'Email the customer when this business confirms an order.' },
              ] },
              { title: 'Invoicing', subtitle: 'Read by the product when it issues a document for this business.', fields: invoicingFields },
              { title: 'Integrations', subtitle: undefined, fields: integrationFields(profile, integrations.data ?? []) },
            ] as (Section & { subtitle?: string })[]
          ).map((section) => (
            <HubSection key={section.title} title={section.title} subtitle={section.subtitle} action={sectionAction(section)}>
              {section.title === 'Integrations' && <LoadState {...integrations} />}
              <SettingsList fields={section.fields} value={profile} />
            </HubSection>
          ))}

          <ProfilePhotos key={id} profile={profile} canUpdate={canUpdate} onSaved={load.reload} />
          <ProfileTexts id={id} canUpdate={canUpdate} />
          <ProfileCategories id={id} productCode={profile.productCode} canUpdate={canUpdate} onSaved={checklist.reload} />
        </>
      )}
      {edit && profile && (
        <EntityDialog
          title={`${edit.title} · ${profile.productName}`}
          value={profile}
          fields={edit.title === 'Integrations' ? integrationFields(profile, integrations.data ?? []) : edit.fields}
          renderExtra={edit.title === 'Public profile' ? (value, onChange, disabled) => (
            <ProfileLocationSearch value={value} onChange={onChange} disabled={disabled} />
          ) : undefined}
          close={() => setEdit(null)}
          validate={(value) => validateProfile(value, hasPrimaryCategory)}
          save={async (value) => {
            await write(apiPath(id), toRequest(value));
            load.reload();
            checklist.reload();
          }}
        />
      )}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this profile?"
        message="Its public page, texts and categories are removed. The product itself stays enabled for the organization."
        confirmLabel="Delete profile"
        busy={busy}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setBusy(true);
          setError(null);
          try {
            await write(apiPath(id), null, 'delete');
            navigate(PROFILES_PATH, { replace: true });
          } catch (caught) {
            setError(errorMessage(caught));
            setConfirmDelete(false);
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

/** Read-only view of a section's fields. */
function SettingsList({ fields, value }: { fields: Field[]; value: Values }) {
  const show = (field: Field): ReactNode => {
    const v = value[field.key];
    if (field.type === 'boolean') return v === true ? 'Yes' : 'No';
    if (v === null || v === undefined || v === '') return <Typography component="span" color="text.disabled">Not set</Typography>;
    if (field.type === 'select') return field.options?.find((option) => option.value === String(v))?.label ?? String(v);
    return String(v);
  };
  return (
    <Box
      component="dl"
      className="hub-panel"
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(160px, 240px) 1fr' }, columnGap: 3, rowGap: 1, m: 0 }}
    >
      {fields.map((field) => (
        <Box key={field.key} sx={{ display: 'contents' }}>
          <Typography component="dt" color="text.secondary">
            {field.label}
          </Typography>
          <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {show(field)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ProfileTexts({ id, canUpdate }: { id: string; canUpdate: boolean }) {
  const load = useHubLoad(
    useCallback((signal: AbortSignal) => read<CompanyText[]>(`${apiPath(id)}/texts`, undefined, signal), [id]),
  );
  const [language, setLanguage] = useState('al');
  const [edit, setEdit] = useState<Values | null>(null);
  const current = load.data?.find((item) => item.languageCode === language);
  return (
    <HubSection title="Public texts" subtitle="Tagline and description shown on this business's page, per language.">
      <LoadState {...load} />
      {load.data && (
        <div className="hub-panel">
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
            <TextField select label="Language" value={language} onChange={(event) => setLanguage(event.target.value)}>
              {['al', 'en', 'it'].map((code) => (
                <MenuItem key={code} value={code}>
                  {code.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<EditOutlined />}
              disabled={!canUpdate}
              onClick={() => setEdit({ languageCode: language, tagline: current?.tagline ?? '', about: current?.about ?? '' })}
            >
              Edit text
            </Button>
          </Stack>
          <Typography sx={{ fontWeight: 600 }}>{current?.tagline ?? 'No tagline yet.'}</Typography>
          <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 1, maxWidth: '72ch' }}>
            {current?.about}
          </Typography>
        </div>
      )}
      {edit && (
        <EntityDialog
          title={`Public texts · ${String(edit.languageCode).toUpperCase()}`}
          value={edit}
          close={() => setEdit(null)}
          fields={[
            { key: 'tagline', label: 'Tagline', maxLength: 300 },
            { key: 'about', label: 'About', type: 'multiline', maxLength: 4000 },
          ]}
          save={async (value) => {
            await write(`${apiPath(id)}/texts`, {
              languageCode: edit.languageCode,
              tagline: value.tagline || null,
              about: value.about || null,
            });
            load.reload();
          }}
        />
      )}
    </HubSection>
  );
}

function ProfileCategories({
  id,
  productCode,
  canUpdate,
  onSaved,
}: {
  id: string;
  productCode: string;
  canUpdate: boolean;
  onSaved: () => void;
}) {
  const load = useHubLoad(
    useCallback(
      (signal: AbortSignal) => read<{ categoryCode: string; isPrimary: boolean }[]>(`${apiPath(id)}/categories`, undefined, signal),
      [id],
    ),
  );
  const taxonomy = useHubLoad(useCallback(() => publicApi.categories('en'), []));
  const [edit, setEdit] = useState<Values | null>(null);
  // A profile is one product's business, so it chooses among that product's categories only.
  const leaves = taxonomy.data?.filter((group) => group.productCode === productCode).flatMap((group) => group.leaves) ?? [];
  return (
    <HubSection title="Categories" subtitle="Where this business appears in the directory.">
      <LoadState {...load} />
      <LoadState {...taxonomy} />
      {load.data && taxonomy.data && (
        <div className="hub-panel">
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {load.data.length === 0 && <Typography color="text.secondary">No categories chosen yet.</Typography>}
            {load.data.map((item) => (
              <Chip
                key={item.categoryCode}
                color={item.isPrimary ? 'primary' : 'default'}
                variant={item.isPrimary ? 'filled' : 'outlined'}
                label={`${leaves.find((leaf) => leaf.code === item.categoryCode)?.name ?? item.categoryCode}${item.isPrimary ? ' · Primary' : ''}`}
              />
            ))}
          </Stack>
          <Button
            variant="outlined"
            startIcon={<EditOutlined />}
            disabled={!canUpdate || leaves.length === 0}
            onClick={() =>
              setEdit({
                ...Object.fromEntries(leaves.map((leaf) => [leaf.code, load.data?.some((item) => item.categoryCode === leaf.code) ?? false])),
                primary: load.data?.find((item) => item.isPrimary)?.categoryCode ?? '',
              })
            }
          >
            Edit categories
          </Button>
          {leaves.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              The directory has no categories for this product yet.
            </Typography>
          )}
        </div>
      )}
      {edit && (
        <EntityDialog
          title="Categories"
          value={edit}
          close={() => setEdit(null)}
          fields={[
            {
              key: 'primary',
              label: 'Primary category',
              type: 'select',
              required: true,
              options: leaves.map((leaf) => ({ value: leaf.code, label: leaf.name })),
            },
            ...leaves.map((leaf) => ({ key: leaf.code, label: leaf.name, type: 'boolean' as const })),
          ]}
          validate={(value) => (value[String(value.primary)] !== true ? 'Select the primary category in your categories too.' : null)}
          save={async (value) => {
            await write(`${apiPath(id)}/categories`, {
              categories: leaves
                .filter((leaf) => value[leaf.code] === true)
                .map((leaf) => ({ categoryCode: leaf.code, isPrimary: leaf.code === value.primary })),
            });
            load.reload();
            onSaved();
          }}
        />
      )}
    </HubSection>
  );
}

// ---------------------------------------------------------------------------------------------
// Product listings (what each product publishes for the organization)
// ---------------------------------------------------------------------------------------------

export function OrganizationProfileListingsPage() {
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.listings(signal), []));
  return (
    <>
      <HubTitle title="Product listings" subtitle="Each product publishes its own listing. Edit availability in the product app." />
      <LoadState {...load} />
      {load.data && (
        <EntityGrid
          rows={load.data}
          rowKey={(row) => row.productCode}
          columns={[
            { label: 'Product', render: (row) => row.productCode },
            {
              label: 'Published',
              render: (row) => (
                <Chip size="small" color={row.isPublished ? 'success' : 'default'} label={row.isPublished ? 'Published' : 'Hidden'} />
              ),
            },
            { label: 'Public path', render: (row) => row.publicPath },
            {
              label: 'Booking / shop',
              render: (row) => [row.hasOnlineBooking && 'Booking', row.hasShop && 'Shop'].filter(Boolean).join(' · '),
            },
            { label: 'Items', render: (row) => row.itemCount },
            { label: 'From', render: (row) => (row.minPrice === null ? '—' : `${row.minPrice} ${row.currencyCode}`) },
            { label: 'Updated', render: (row) => new Date(row.updatedAt).toLocaleString() },
          ]}
          empty="No product publishes a listing for this organization yet."
        />
      )}
    </>
  );
}
